import * as THREE from "three";
import JSZip from "jszip";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter.js";

const MATERIAL_NAME_MAP = {
  "21091": "internal_color",
  "21027": "shutter_color",
};

const getBaseName = (name) => String(name || "").replace(/\.\d{3,}$/, "");

const toSafeFileName = (name) =>
  String(name || "")
    .trim()
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .replace(/-+/g, "-");

// "2 door wardrobe/D1/D2/HP1" -> "2 door wardrobe"
export const moduleGlbBaseName = (name) => {
  const text = String(name || "").trim();
  const slashIndex = text.indexOf("/");
  return slashIndex === -1 ? text : text.slice(0, slashIndex).trim();
};

const getRoomModules = (currentRoom) => {
  const standaloneModules = (currentRoom?.modules || []).filter(
    (module) => module?.template
  );
  const groupedModules = (currentRoom?.groups || []).flatMap((group) =>
    (group?.modules || []).filter((module) => module?.template)
  );
  return [...standaloneModules, ...groupedModules];
};

const exportModuleAsGlb = (moduleClone) =>
  new Promise((resolve, reject) => {
    const exportRoot = new THREE.Group();
    exportRoot.add(moduleClone);
    exportRoot.position.set(0, 0, 0);
    exportRoot.rotation.set(0, 0, 0);
    // Scene units are mm; export GLB in meters.
    exportRoot.scale.set(0.001, 0.001, 0.001);
    exportRoot.updateMatrixWorld(true);

    const materialByBaseName = new Map();
    exportRoot.traverse((obj) => {
      if (!obj?.isMesh || !obj.material) return;

      const normalizeMaterial = (mat) => {
        if (!mat || mat.type !== "MeshStandardMaterial") return mat;
        const baseName = getBaseName(mat.name);
        if (!baseName) return mat;
        const mappedName = MATERIAL_NAME_MAP[baseName] || baseName;
        mat.name = mappedName;

        if (!materialByBaseName.has(mappedName)) {
          materialByBaseName.set(mappedName, mat);
          return mat;
        }
        return materialByBaseName.get(mappedName);
      };

      if (Array.isArray(obj.material)) {
        obj.material = obj.material.map(normalizeMaterial);
      } else {
        obj.material = normalizeMaterial(obj.material);
      }
    });

    const exporter = new GLTFExporter();
    exporter.parse(
      exportRoot,
      (result) => {
        if (result instanceof ArrayBuffer) resolve(result);
        else reject(new Error("GLB export returned non-binary data."));
      },
      (error) => reject(error),
      { binary: true, onlyVisible: true, trs: false }
    );
  });

export const buildRoomModulesZipName = (currentRoom) => {
  const roomFileKey = currentRoom?.name || currentRoom?.id || "room";
  const roomFileName = String(roomFileKey)
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9-_]/g, "")
    .replace(/-+/g, "-");
  return `${roomFileName || "room"}-modules.zip`;
};

export const exportRoomModulesAsGlbZip = async ({ currentRoom, threeScene }) => {
  const roomModules = getRoomModules(currentRoom);
  if (roomModules.length === 0) {
    throw new Error("No modules found in the room to export.");
  }

  const sceneModuleById = new Map();
  threeScene.traverse((obj) => {
    const moduleId = obj?.userData?.module?.id;
    if (!moduleId || sceneModuleById.has(moduleId)) return;
    sceneModuleById.set(moduleId, obj);
  });

  threeScene.updateMatrixWorld(true);
  const skippedModuleIds = [];
  const uniqueModuleIds = new Set();
  const modulesToExport = [];

  roomModules.forEach((module) => {
    if (!module?.id || uniqueModuleIds.has(module.id)) return;
    uniqueModuleIds.add(module.id);

    const moduleObject = sceneModuleById.get(module.id);
    if (!moduleObject) {
      skippedModuleIds.push(module.id);
      return;
    }

    moduleObject.updateWorldMatrix(true, true);
    const moduleClone = moduleObject.clone(true);
    moduleClone.applyMatrix4(moduleObject.matrixWorld);
    // Keep orientation/scale, author each module GLB at origin.
    moduleClone.position.set(0, 0, 0);
    moduleClone.updateMatrixWorld(true);

    const moduleName =
      module?.order_item?.name ||
      module?.orderItem?.name ||
      module?.name ||
      module?.label ||
      `module-${module.id}`;

    modulesToExport.push({
      moduleId: module.id,
      moduleName: moduleGlbBaseName(moduleName),
      moduleClone,
    });
  });

  if (modulesToExport.length === 0) {
    throw new Error("Could not find room modules in the 3D scene.");
  }

  const zip = new JSZip();
  for (const { moduleId, moduleName, moduleClone } of modulesToExport) {
    const glbBuffer = await exportModuleAsGlb(moduleClone);
    const safeName = toSafeFileName(moduleName) || `module-${moduleId}`;
    zip.file(`${safeName}.glb`, glbBuffer);
  }

  const zipBlob = await zip.generateAsync({ type: "blob" });
  return {
    zipBlob,
    exportedCount: modulesToExport.length,
    skippedModuleIds,
  };
};
